import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BookUser, Loader2, Trash2, Paperclip, CheckCircle2, XCircle, Mail, Plus, ChevronRight, Lock, RotateCcw, Download, RefreshCw } from "lucide-react";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Rubrica() {
  const [contacts, setContacts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newContact, setNewContact] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [openCat, setOpenCat] = useState(null);
  const [busy, setBusy] = useState("");
  const [lockedLists, setLockedLists] = useState({});
  const [rubricaStats, setRubricaStats] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [showSub, setShowSub] = useState(() => localStorage.getItem("nl_show_sub") === "1");
  const hideSub = () => { setShowSub(false); localStorage.removeItem("nl_show_sub"); };
  const revealSub = () => { setShowSub(true); localStorage.setItem("nl_show_sub", "1"); };

  const loadContacts = () => api.get("/newsletter/contacts").then((r) => setContacts(r.data)).catch(() => {});
  const loadCategories = () => api.get("/newsletter/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  const loadStats = () => api.get("/newsletter/rubrica-analysis").then((r) => setRubricaStats(r.data)).catch(() => {});
  const loadLastSync = () => api.get("/import/fier-sync-log?kind=anagrafiche_full").then((r) => setLastSync((r.data || [])[0] || null)).catch(() => {});

  useEffect(() => { loadContacts(); loadCategories(); loadStats(); loadLastSync(); }, []);
  useEffect(() => { if (categories.length && !categories.find((c) => c.slug === selectedCat)) setSelectedCat(categories.find((c) => !c.locked)?.slug || ""); }, [categories, selectedCat]);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const runSync = async () => {
    setSyncing(true); setSyncMsg("Avvio sincronizzazione…");
    try { await api.post("/import/fier-full-sync-start"); }
    catch (err) {
      if (err.response?.status === 409) { setSyncMsg("Sincronizzazione già in corso…"); }
      else { toast.error("Impossibile avviare la sincronizzazione"); setSyncing(false); return; }
    }
    const poll = setInterval(async () => {
      try {
        const r = await api.get("/import/fier-full-sync-status");
        const st = r.data || {};
        setSyncMsg(st.message || "Sincronizzazione in corso…");
        if (st.finished || !st.running) {
          clearInterval(poll); setSyncing(false);
          if (st.error) toast.error("Sincronizzazione terminata con errori");
          else toast.success("Rubrica aggiornata dal gestionale Fier");
          loadCategories(); loadStats(); loadLastSync();
        }
      } catch { clearInterval(poll); setSyncing(false); }
    }, 3000);
  };

  const contactCatOf = (c) => c.category || c.group || "generici";
  const subscribeUrl = `${PUBLIC_BASE}/api/newsletter/subscribe`;

  const addContact = async () => {
    const emails = newContact.split(/[,\n;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!emails.length) return;
    if (!selectedCat) { toast.error("Crea o seleziona una categoria"); return; }
    setBusy("contact");
    try { const r = await api.post("/newsletter/contacts", { emails, category: selectedCat }); toast.success(`${r.data.added} contatti aggiunti`); setNewContact(""); setOpenCat(selectedCat); loadContacts(); loadCategories(); loadStats(); }
    catch (err) { toast.error("Errore"); } finally { setBusy(""); }
  };
  const delContact = async (id) => { await api.delete(`/newsletter/contacts/${id}`); loadContacts(); loadCategories(); loadStats(); };
  const importContacts = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!selectedCat) { toast.error("Crea o seleziona una categoria"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    setBusy("import");
    try { const r = await api.post(`/newsletter/contacts/import?category=${encodeURIComponent(selectedCat)}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${r.data.added} contatti importati`); setOpenCat(selectedCat); loadContacts(); loadCategories(); loadStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import fallito"); }
    finally { setBusy(""); e.target.value = ""; }
  };
  const createCategory = async () => {
    const name = newCatName.trim(); if (!name) return;
    setBusy("newcat");
    try { const r = await api.post("/newsletter/categories", { name }); toast.success("Categoria creata"); setNewCatName(""); setSelectedCat(r.data.slug); loadCategories(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(""); }
  };
  const deleteCategory = async (slug, cnt) => {
    if (!window.confirm(cnt ? `Eliminare la categoria e i suoi ${cnt} contatti?` : "Eliminare la categoria?")) return;
    try { await api.delete(`/newsletter/categories/${slug}`); toast.success("Categoria eliminata"); if (openCat === slug) setOpenCat(null); loadCategories(); loadContacts(); loadStats(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };
  const reactivate = async (email) => {
    if (!email) return;
    try {
      await api.post("/newsletter/reactivate", { email });
      toast.success("Contatto riattivato — tornerà contattabile");
      const r = await api.get("/newsletter/unsubscribed");
      setLockedLists((m) => ({ ...m, __unsubscribed__: r.data || [] }));
      loadCategories(); loadStats();
    } catch { toast.error("Riattivazione fallita"); }
  };
  const reactivateAll = async (count) => {
    if (!window.confirm(`Riattivare tutti i ${count} contatti soppressi/disiscritti? Torneranno contattabili e potranno ricevere newsletter.`)) return;
    try {
      const r = await api.post("/newsletter/reactivate", { all: true });
      toast.success(`${r.data.reactivated} contatti riattivati`);
      setLockedLists((m) => ({ ...m, __unsubscribed__: [] }));
      loadCategories(); loadStats();
    } catch { toast.error("Riattivazione fallita"); }
  };
  const exportCategory = async (slug, name) => {
    setBusy(`exp-${slug}`);
    try {
      const r = await api.get(`/newsletter/categories/${encodeURIComponent(slug)}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rubrica_${(name || slug).replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
    finally { setBusy(null); }
  };
  const openCategory = async (cat) => {
    const willOpen = openCat !== cat.slug;
    setOpenCat(willOpen ? cat.slug : null);
    if (willOpen && cat.locked && !lockedLists[cat.slug]) {
      try {
        if (cat.slug === "__unsubscribed__") {
          const r = await api.get("/newsletter/unsubscribed");
          setLockedLists((m) => ({ ...m, [cat.slug]: r.data || [] }));
        } else {
          const q = cat.slug === "__partners__" ? "?partners=true" : cat.slug === "__clients__" ? "?clients=true" : cat.slug === "__prospects__" ? "?prospects=true" : "?agents=true";
          const r = await api.get(`/newsletter/audience-list${q}`);
          setLockedLists((m) => ({ ...m, [cat.slug]: r.data.recipients || [] }));
        }
      } catch { toast.error("Impossibile caricare l'elenco"); }
    }
  };

  return (
    <div className="space-y-6" data-testid="rubrica-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tight"><BookUser className="h-7 w-7 text-primary" /> Rubrica contatti</h1>
        <p className="text-sm text-muted-foreground">Crea categorie, importa contatti (Excel/CSV) e clicca una categoria per vederne l'elenco. Le rubriche sono selezionabili come destinatari nella Newsletter.</p>
      </div>

      <Card className="p-5" data-testid="contacts-card">
        {/* Auto-acquire site subscribers */}
        {showSub ? (
        <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="subscribe-info">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-xs font-semibold text-primary">Acquisizione automatica “Iscritti al sito”</div>
            <button onClick={hideSub} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="hide-subscribe">Nascondi ✕</button>
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">Collega il form del sito a questo endpoint: ogni iscritto entrerà automaticamente nella categoria <b>Iscritti al sito</b>. Una volta collegato, puoi nasconderlo.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-black/40 px-2 py-1 text-[11px]" data-testid="subscribe-url">POST {subscribeUrl}</code>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(subscribeUrl); toast.success("URL copiato"); }} data-testid="copy-subscribe-url">Copia</Button>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">Body JSON: {"{ \"email\": \"...\", \"name\": \"...\" }"}</p>
        </div>
        ) : (
          <button onClick={revealSub} className="mb-4 text-[11px] text-primary hover:underline" data-testid="show-subscribe">Mostra endpoint iscrizione sito</button>
        )}

        {/* Create category */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createCategory(); }} placeholder="Nome nuova categoria (es. Fornitori)" className="max-w-xs" data-testid="new-cat-input" />
          <Button size="sm" variant="outline" onClick={createCategory} disabled={busy === "newcat"} data-testid="create-cat-btn">{busy === "newcat" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}Crea categoria</Button>
        </div>

        {/* Add / import into a category */}
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-input bg-muted/20 p-2">
          <span className="text-xs text-muted-foreground">Aggiungi in:</span>
          <select value={selectedCat} onChange={(e) => setSelectedCat(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="contact-cat-select">
            {categories.filter((c) => !c.locked).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <Input value={newContact} onChange={(e) => setNewContact(e.target.value)} placeholder="email1@x.it, email2@y.it" className="max-w-[240px]" data-testid="new-contact-input" />
          <Button size="sm" onClick={addContact} disabled={busy === "contact"} data-testid="add-contact-btn">{busy === "contact" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi"}</Button>
          <input id="csv-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importContacts} data-testid="import-file-input" />
          <Button variant="outline" size="sm" onClick={() => document.getElementById("csv-input").click()} disabled={busy === "import"} data-testid="import-contacts-btn">{busy === "import" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />} Importa CSV/Excel</Button>
        </div>

        {/* CRM auto-sync reminder + manual refresh */}
        <div className="mb-4 flex flex-col gap-2 rounded-lg border border-white/10 bg-black/20 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid="crm-sync-reminder">
          <div className="text-[11px] text-muted-foreground">
            <div className="flex items-center gap-1.5 font-medium text-foreground/90"><RefreshCw className="h-3.5 w-3.5 text-primary" />Le categorie CRM si aggiornano automaticamente ogni notte dal gestionale Fier</div>
            <div className="mt-0.5" data-testid="crm-last-sync">Ultima sincronizzazione: <b className="text-foreground/90">{lastSync?.created_at ? fmtDate(lastSync.created_at) : "—"}</b></div>
            {syncing && syncMsg && <div className="mt-0.5 text-primary" data-testid="crm-sync-msg">{syncMsg}</div>}
          </div>
          <Button size="sm" variant="outline" onClick={runSync} disabled={syncing} className="shrink-0" data-testid="crm-sync-now-btn">
            {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Aggiorna ora
          </Button>
        </div>

        {/* Categories overview */}
        <div className="space-y-2" data-testid="categories-list">
          {categories.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="no-categories">Nessuna categoria. Creane una qui sopra.</p> : categories.map((cat) => {
            const open = openCat === cat.slug;
            const list = cat.locked ? (lockedLists[cat.slug] || []) : contacts.filter((c) => contactCatOf(c) === cat.slug);
            return (
              <div key={cat.slug} className="rounded-lg border border-white/10 bg-black/30" data-testid={`cat-row-${cat.slug}`}>
                <div className="flex items-center justify-between px-3 py-2">
                  <button onClick={() => openCategory(cat)} className="flex items-center gap-2 text-sm font-medium" data-testid={`cat-open-${cat.slug}`}>
                    <ChevronRight className={`h-4 w-4 text-primary transition-transform ${open ? "rotate-90" : ""}`} />
                    {cat.name} <span className="text-primary">({cat.count}{cat.locked && cat.total != null ? ` di ${cat.total} con email` : ""})</span>
                    {cat.locked
                      ? <span className="flex items-center gap-1 rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground"><Lock className="h-2.5 w-2.5" />CRM</span>
                      : cat.system ? <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-primary">sistema</span> : null}
                  </button>
                  <div className="flex items-center gap-2">
                    {cat.count > 0 && <button onClick={() => exportCategory(cat.slug, cat.name)} disabled={busy === `exp-${cat.slug}`} className="text-muted-foreground hover:text-primary" title="Scarica CSV" data-testid={`export-cat-${cat.slug}`}>{busy === `exp-${cat.slug}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>}
                    {!cat.system && !cat.locked && <button onClick={() => deleteCategory(cat.slug, cat.count)} className="text-red-400" data-testid={`del-cat-${cat.slug}`}><Trash2 className="h-4 w-4" /></button>}
                    {cat.slug === "__unsubscribed__" && cat.count > 0 && <button onClick={() => reactivateAll(cat.count)} className="flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/40 px-2.5 py-1 text-[11px] font-semibold text-emerald-400 hover:bg-emerald-500/10" data-testid="reactivate-all-btn"><RotateCcw className="h-3 w-3" />Riattiva tutti</button>}
                  </div>
                </div>
                {open && (
                  <div className="border-t border-white/10 p-2" data-testid={`cat-contacts-${cat.slug}`}>
                    {list.length === 0 ? <p className="px-2 py-1 text-xs text-muted-foreground">{cat.locked ? "Nessun contatto con email nel CRM." : "Nessun contatto in questa categoria."}</p> : (
                      <div className="grid gap-1.5 sm:grid-cols-2">
                        {list.map((c, i) => (
                          <div key={c.id || c.email || i} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs" data-testid={`contact-${c.id || c.email}`}>
                            <span className="flex items-center gap-1.5 truncate">
                              {cat.locked ? <Mail className="h-3.5 w-3.5 text-muted-foreground" /> : (c.unsubscribed ? <XCircle className="h-3.5 w-3.5 text-red-400" title="Disiscritto" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />)}
                              <span className="truncate">{c.name ? `${c.name} · ` : ""}{c.email}</span>
                            </span>
                            {cat.slug === "__unsubscribed__" && <button onClick={() => reactivate(c.email)} className="shrink-0 rounded-full border border-emerald-500/40 px-2 py-0.5 text-[10px] font-semibold text-emerald-400 hover:bg-emerald-500/10" data-testid={`reactivate-${c.email}`}><RotateCcw className="mr-1 inline h-3 w-3" />Riattiva</button>}
                            {!cat.locked && <button onClick={() => delContact(c.id)} className="text-red-400" data-testid={`del-contact-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {rubricaStats && (
          <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="rubrica-analysis">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Analisi totale contatti</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div data-testid="rubrica-total"><div className="text-lg font-bold">{rubricaStats.total.toLocaleString("it-IT")}</div><div className="text-[11px] text-muted-foreground">Totale (somma categorie)</div></div>
              <div data-testid="rubrica-duplicates"><div className="text-lg font-bold text-amber-400">−{rubricaStats.duplicates.toLocaleString("it-IT")}</div><div className="text-[11px] text-muted-foreground">Doppioni (email ripetute)</div></div>
              <div data-testid="rubrica-unique"><div className="text-lg font-bold text-emerald-400">{rubricaStats.unique.toLocaleString("it-IT")}</div><div className="text-[11px] text-muted-foreground">Destinatari unici</div></div>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Ogni email viene inviata una sola volta: i doppioni tra categorie diverse vengono contati una volta sola.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
